import { initialDataLayer } from './datalayer';
import merge from 'lodash.merge';

declare global {
  interface Window {
    adobeDataLayer: DeepPartialWithNullsAsAny<typeof initialDataLayer>[];
  }
}

export type DeepPartialWithNullsAsAny<T> = {
  [K in keyof T]?: T[K] extends object
    ? DeepPartialWithNullsAsAny<T[K]>
    : T[K] extends null
    ? string | number | null
    : T[K];
};

export type AnalyticsPageName =
  | 'Continue: Authentication'
  | 'Continue: Link expired'
  | 'Landing page'
  | 'Document upload'
  | 'Acknowledgement'
  | 'Legitimation timeout'
  | 'Error page';

export type AnalyticsViewChange =
  | 'TAN input'
  | 'Invalid TAN'
  | 'Link expired'
  | 'Landing page'
  | 'Document upload'
  | 'Upload error'
  | 'API error'
  | 'Acknowledgement'
  | 'Legitimation timeout'
  | 'Error page';

export const analyticsEvents: { [key in AnalyticsViewChange]?: { eventType: string; eventAction: string } } = {
  'TAN input': { eventType: 'Application data', eventAction: 'Reloaded' },
  'Landing page': { eventType: 'Product journey', eventAction: 'Start' },
  'Document upload': { eventType: 'Legitimation', eventAction: 'Success' },
  Acknowledgement: { eventType: 'Contract Documents', eventAction: 'Upload' }
};
export const analyticsEventsAfterSaveAndContinue: {
  [key in AnalyticsViewChange]?: { eventType: string; eventAction: string };
} = {
  'Landing page': { eventType: 'Product journey', eventAction: 'Start' },
  'Document upload': { eventType: 'Product journey', eventAction: 'Start' },
  Acknowledgement: { eventType: 'Product journey', eventAction: 'Start' }
};

class TrackingService {
  setInitialJourneyData() {
    window.adobeDataLayer = window.adobeDataLayer || [];
    const initialAnalyticsPayload = merge(initialDataLayer, {
      core: {
        stagingEnvironment: process.env.REACT_APP_STAGE?.toUpperCase(),
        attributes: {
          brand: process.env.REACT_APP_WEBSITE_BRAND?.toUpperCase()
        },
        pageInfo: {
          language: 'en',
          market: 'UK'
        },
        category: {
          primaryCategory: 'Portal',
          secondaryCategory: 'Underwriting'
        }
      }
    });
    window.adobeDataLayer.push(initialAnalyticsPayload);
  }

  extendWithInitialTransactionData(data: typeof window.adobeDataLayer['0']) {
    window.adobeDataLayer.push(data);
  }

  trackPageView(pageName: AnalyticsPageName, viewChange: AnalyticsViewChange) {
    let isFirstPageAfterLogin = false;
    const lastLoginDate = window.sessionStorage.getItem('scDate');
    if (lastLoginDate && +new Date() - Number(lastLoginDate) < 2_000) {
      // if S&C login was done less than 2s ago
      isFirstPageAfterLogin = true;
    }
    const eventInfo = isFirstPageAfterLogin
      ? analyticsEventsAfterSaveAndContinue[viewChange]
      : analyticsEvents[viewChange];
    window.adobeDataLayer = window.adobeDataLayer || [];
    const eventPayload: typeof window.adobeDataLayer['0'] = {
      event: 'page',
      core: {
        attributes: {
          viewChange: viewChange
        },
        pageInfo: {
          pageName: pageName
        }
      }
    };
    if (eventInfo) {
      eventPayload.eventInfo = [eventInfo];
    }

    window.adobeDataLayer.push(eventPayload);
  }
}

export const trackingService = new TrackingService();
